import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { ExclamationDiamondFill } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { OptionsType, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';

import { pathnames, requestEquipments } from '../../api';
import { getAxiosErrorResponse } from '../../api/utils';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import CustomerEquipment, {
  CustomerEquipmentPropType,
} from '../../models/data/CustomerEquipment';

const FormatOptionLabel = ({
  equipmentName,
  officeName,
}: CustomerEquipment) => (
  <div className="d-flex align-items-baseline">
    <small className="text-secondary">{officeName}</small>
    <span className="mx-4">{equipmentName}</span>
  </div>
);

export interface SelectEquipmentsProps {
  className?: string;
  isDisabled?: boolean;
  customerId?: number;
  selectedCustomerEquipment?: CustomerEquipment | null;
  onChange?: (customerEquipment: CustomerEquipment | undefined | null) => void;
}

const SelectEquipments: React.FunctionComponent<SelectEquipmentsProps> = ({
  className,
  isDisabled,
  customerId,
  selectedCustomerEquipment,
  onChange,
}) => {
  const context = useContext(AuthenticatedContext);
  // ---------------------------------------------------------------------
  // Create WEB API URL.
  const history = useHistory();
  const url = history.createHref({ pathname: pathnames.equipments });
  // ---------------------------------------------------------------------
  // Status
  const [cacheVersion, setCacheVersion] = useState(1);
  const [defaultOptions, setDefaultOptions] = useState<
    OptionsType<CustomerEquipment>
  >([]);
  const [selectedOption, setSelectedOption] = useState<
    ValueType<CustomerEquipment, false>
  >(selectedCustomerEquipment);
  // ---------------------------------------------------------------------
  // Callbacks & Functions
  const onChangeSelect = (option: ValueType<CustomerEquipment, false>) => {
    setSelectedOption(option);
    if (onChange != null) {
      onChange(option);
    }
  };
  const toastError = (error: unknown) => {
    const res = getAxiosErrorResponse(error);
    toast.error(
      () => {
        return (
          <>
            <ExclamationDiamondFill />
            <span>{` 顧客設備 取得失敗`}</span>
            <small>{` (${res?.status ?? '応答なし'})`}</small>
          </>
        );
      },
      {
        onClose: () => {
          if (res != null && res.status >= 400 && res.status < 500) {
            // 認証エラーの可能性大、ログインチェック（期限切れの場合ログイン画面へ遷移する。はず）
            context.login().catch(() => {
              // do nothing.
            });
          }
        },
      },
    );
  };
  const loadOptions = (
    eqname: string,
  ): Promise<OptionsType<CustomerEquipment>> => {
    if (isDisabled) {
      return Promise.resolve([]);
    }
    return requestEquipments(url, {
      eqname: eqname === '' ? undefined : eqname,
      id: customerId,
    })
      .then(({ data: equipments }) => {
        return equipments;
      })
      .catch((error) => {
        toastError(error);
        throw error;
        // return [];
      });
  };
  // ---------------------------------------------------------------------
  // Effects
  //  --- Initialize
  useEffect(() => {
    setSelectedOption(selectedCustomerEquipment);
  }, [selectedCustomerEquipment]);
  //  --- Only re-subscribe if props.isDisabled or props.customerId changes
  useEffect(() => {
    let unmounted = false;
    setCacheVersion(cacheVersion + 1); // Purge cache request.
    loadOptions('')
      .then((equipments) => {
        if (!unmounted) {
          const [selected = null] = equipments.filter((eq) => {
            return (
              eq.customerEquipmentId ===
              selectedCustomerEquipment?.customerEquipmentId
            );
          });
          setDefaultOptions(equipments);
          setSelectedOption(selected);
        }
      })
      .catch(() => {
        if (!unmounted) {
          setDefaultOptions([]);
          setSelectedOption(null);
        }
      });
    return () => {
      unmounted = true;
    };
  }, [isDisabled, customerId]);
  // ---------------------------------------------------------------------
  // Components
  return (
    <AsyncSelect
      className={className}
      placeholder="顧客設備を選択してください"
      isDisabled={isDisabled}
      defaultOptions={defaultOptions}
      formatOptionLabel={FormatOptionLabel}
      cacheOptions={cacheVersion}
      loadOptions={loadOptions}
      value={selectedOption}
      onChange={onChangeSelect}
      getOptionLabel={(opt) => opt.equipmentName}
      getOptionValue={(opt) => String(opt.customerEquipmentId)}
      isClearable
      styles={{
        // fix #1085
        menu: (zzz) => {
          return {
            ...zzz,
            zIndex: 9999,
          };
        },
      }}
    />
  );
};

SelectEquipments.propTypes = {
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  customerId: PropTypes.number,
  selectedCustomerEquipment: CustomerEquipmentPropType,
  onChange: PropTypes.func,
};

SelectEquipments.defaultProps = {
  className: undefined,
  isDisabled: undefined,
  customerId: undefined,
  selectedCustomerEquipment: undefined,
  onChange: undefined,
};

export default SelectEquipments;
